import React, { useMemo } from 'react'
import { withAsync } from '@/components/Async'
import { Redirect } from 'react-router'
import type { RouteConfig } from 'react-router-config'
import { renderRoutes } from 'react-router-config'
import { absRoutes, travelRoutes } from '@/utils'
import { usePermission } from './interceptors/permission'
import redirect from './redirect'
import error from './error'
import admin from './admin'
import user from './user'
import middleware from './middleware'

/**
 * 增加Not Match 404 页面路由
 * @param routes 路由
 */
export function addNotMatchRoute(routes: RouteConfig[]): RouteConfig[] {
  const newRoutes = routes.map((item) => {
    // 为什么不用在item里面注入notfoundpage，是因为有一个/path，会自动注入404
    if (item.routes && item.routes.length > 0) {
      item.routes.push({
        path: '/*',
        component: () => <Redirect to="/notFoundPage" />,
        exact: true,
      })
      return { ...item, routes: addNotMatchRoute(item.routes) }
    } else {
      return item
    }
  })
  return newRoutes
}

export const routes = addNotMatchRoute(
  absRoutes([
    {
      path: '/noAuth',
      component: withAsync(() => import(/* webpackChunkName: "/noAuth" */ '@/containers/NotAuthPage')),
      exact: true,
    },
    // 这个路由和'/noAuth'几乎一样，只是做了“文案展示”、“路由跳转”的限制，
    // showType等于simple时，不会跳转到 /order/list，同时不展示部分文案
    //TODO: 这里应该用一个新的名字并且放在admin才对，否则出现大量重复的处理逻辑，例如checkLogin
    {
      path: '/noAuth/:showType',
      component: withAsync(() => import(/* webpackChunkName: "/noAuth" */ '@/containers/NotAuthPage')),
    },
    /**
     * TODO: 已下线
     */
    {
      path: '/warehousing/print',
      component: withAsync(() => import(/* webpackChunkName: "warehousing" */ '@/containers/Warehousing/components/Print')),
    },
    {
      path: '/notFoundPage',
      component: withAsync(() => import(/* webpackChunkName: "Error/NotFoundPage" */ '@/containers/NotFoundPage')),
      exact: true,
    },
    ...redirect,
    ...error,
    ...user,
    ...middleware,
    ...admin,
  ])
)

const Router: React.FC = () => {
  /** 路由拦截器 */
  const permissionInterceptor = usePermission()
  const routers = useMemo(() => {
    // 遍历每个路由执行特定逻辑
    const interceptedRoutes = travelRoutes(routes, (route: RouteConfig) => {
      let interceptorRoute = route

      if (typeof route.interceptor === 'function') {
        // route 层面的拦截器
        return route.interceptor(route, {})
      }

      // 权限拦截
      interceptorRoute = permissionInterceptor(route)

      return interceptorRoute
    })
    // 做一层兜底，拦截器增加了特别的路由，却不是绝对路径的
    return absRoutes(interceptedRoutes)
  }, [permissionInterceptor])

  return renderRoutes(routers)
}

Router.displayName = 'Router'
export default Router
